import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { addZohoProducts, getAllTickets, getTicketsByAssignee } from '../../../Redux/actions';
import {
    Avatar, Box, CircularProgress, Grid, IconButton, Pagination,
    Paper, Tab, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Tabs, Tooltip, Typography,
} from '@mui/material';
import { CalendarMonth, ChevronRight } from '@mui/icons-material';
import FilterListIcon from '@mui/icons-material/FilterList';
import SyncIcon from '@mui/icons-material/Sync';
import SearchBox from '../../Common/SearchBox';
import Filter from '../../Common/Filter';
import Loader from '../../Common/Loader';
import { tabStyle, tabListStyle, paginationStyle } from '../../../theme/formStyles';
import { updateParams } from '../../../utils';
import useBackActionDetection from '../../../utils/UseBackActionDetection';

// ── Avatar chip helpers ────────────────────────────────────────────────────────

const AVATAR_COLORS = [
    '#006666', '#008080', '#00838f', '#00695c', '#2e7d32',
    '#1565c0', '#6a1b9a', '#ad1457', '#bf360c', '#4e342e',
];

function avatarColor(str) {
    if (!str) return '#757575';
    let h = 0;
    for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
    return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

function initials(nameOrEmail) {
    if (!nameOrEmail) return '?';
    const base = nameOrEmail.includes('@') ? nameOrEmail.split('@')[0] : nameOrEmail;
    const parts = base.trim().split(/[\s._-]+/);
    return parts.length >= 2
        ? (parts[0][0] + parts[1][0]).toUpperCase()
        : base.slice(0, 2).toUpperCase();
}

function displayName(nameOrEmail) {
    if (!nameOrEmail) return '';
    if (!nameOrEmail.includes('@')) return nameOrEmail;
    return nameOrEmail.split('@')[0].replace(/[._]/g, ' ');
}

function UserChip({ name }) {
    if (!name) {
        return (
            <Typography variant="body2" sx={{ color: '#aaa', fontSize: '12px' }}>
                -
            </Typography>
        );
    }
    const color = avatarColor(name);
    return (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            backgroundColor: `${color}18`, borderRadius: '20px',
            padding: '2px 8px 2px 2px', whiteSpace: 'nowrap',
        }}>
            <Avatar sx={{ width: 22, height: 22, fontSize: '10px', bgcolor: color, flexShrink: 0 }}>
                {initials(name)}
            </Avatar>
            <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#333' }}>
                {displayName(name)}
            </Typography>
        </Box>
    );
}

// ── Status chip ────────────────────────────────────────────────────────────────

const STATUS_PALETTE = {
    'Open':        { bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' },
    'Closed':      { bg: '#bef3fa', color: '#10808f', border: '#0eacc4' },
    'Pending':     { bg: '#fff8e1', color: '#e65100', border: '#ffe082' },
    'In Progress': { bg: '#e8eaf6', color: '#3949ab', border: '#9fa8da' },
    'On Hold':     { bg: '#fff3e0', color: '#ef6c00', border: '#ffcc80' },
};

function StatusChip({ status }) {
    const p = STATUS_PALETTE[status] || { bg: '#f5f5f5', color: '#757575', border: '#e0e0e0' };
    return (
        <Box sx={{
            display: 'inline-block', padding: '3px 0', borderRadius: '4px',
            backgroundColor: p.bg, color: p.color, border: `1px solid ${p.border}`,
            fontSize: '12px', fontWeight: 500, whiteSpace: 'nowrap',
            width: '90px', textAlign: 'center',
        }}>
            {status || '—'}
        </Box>
    );
}

// ── Date cell ──────────────────────────────────────────────────────────────────

function DateCell({ date }) {
    if (!date) {
        return (
            <Typography variant="body2" sx={{ color: '#aaa', fontSize: '12px' }}>
                -
            </Typography>
        );
    }
    const formatted = date.split('T')[0].split('-').reverse().join('/');
    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <CalendarMonth sx={{ fontSize: 15, color: '#c0392b' }} />
            <Typography sx={{ fontSize: '13px' }}>{formatted}</Typography>
        </Box>
    );
}

// ── Main component ─────────────────────────────────────────────────────────────

const HEAD_CELL = {
    fontWeight: 700, fontSize: '11px', color: '#666',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    py: 1.5, borderBottom: '2px solid #e0e0e0', backgroundColor: '#fafafa',
};

const ITEMS_PER_PAGE = 20;

export default function AlertsTickets() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [queryParams, setQueryParams] = useSearchParams();
    const existingParams = Object.fromEntries([...queryParams]);
    const { currentUser: { uniqueId } } = useSelector(st => st);

    const [pageTickets, setPageTickets] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [showLoader, setShowLoader] = useState(true);
    const [showFilterDialog, setShowFilterDialog] = useState(false);
    const [isSearchOrFilterApplied, setIsSearchOrFilterApplied] = useState(false);
    const [tabSwitch, setTabSwitch] = useState(false);
    const [addingZohoPrdctLoader, setAddingZohoPrdctLoader] = useState(false);

    const isBackAction = useBackActionDetection();
    const activeTab = queryParams.get('tab') || 'all-tickets';

    useEffect(() => {
        if (!queryParams.get('tab')) {
            setQueryParams({ tab: 'all-tickets', page: 1 });
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [queryParams]);

    const fetchData = () => {
        setShowLoader(true);
        const params = {
            ...existingParams,
            title: existingParams?.search,
            page: existingParams?.page || 1,
        };

        const action = activeTab === 'my-tickets' ? getTicketsByAssignee : getAllTickets;

        dispatch(action(params)).then((res) => {
            if (res?.status === 200) {
                setPageTickets(res.data?.data || []);
                setTotalCount(res.data?.totalItems ?? 0);
                setTotalPages(res.data?.totalPages ?? 1);
                setCurrentPage(res.data?.currentPage ?? 1);
                setShowLoader(false);
            }
        });
    };

    const updateQueryParams = (params) => {
        const searchParams = updateParams(existingParams, params);
        navigate({ search: searchParams.toString() }, { replace: true });
    };

    const handleChangeTab = (_, newValue) => {
        setQueryParams({ tab: newValue, page: 1 });
        setTabSwitch(true);
    };

    const handleSearch = (value) => {
        updateQueryParams({ search: value, tab: activeTab, page: 1 });
        setIsSearchOrFilterApplied(true);
    };

    const handlePageChange = (_, newPage) => updateQueryParams({ page: newPage });

    const handleTabSwitch = () => setTabSwitch(false);

    const handleTicketClick = (ticketId) => {
        localStorage.setItem('ticketsQueryParams', queryParams.toString());
        navigate(`/tickets/details/${encodeURIComponent(ticketId)}`);
    };

    const handleZohoProductSync = () => {
        setAddingZohoPrdctLoader(true);
        dispatch(addZohoProducts())
            .then(() => setAddingZohoPrdctLoader(false))
            .catch(() => setAddingZohoPrdctLoader(false));
    };

    const isGreenUser = uniqueId?.includes('greenvironment.in') || uniqueId?.includes('greenvironmentindia.com');

    // Group current page's tickets by client (backend ORDER BY client.name keeps them contiguous)
    const groupedTickets = {};
    pageTickets.forEach((ticket) => {
        const key = ticket.scope === 'Announcement' ? 'Announcements' : (ticket.client?.name || 'Unknown');
        if (!groupedTickets[key]) groupedTickets[key] = [];
        groupedTickets[key].push(ticket);
    });

    const clientGroups = Object.entries(groupedTickets);
    const hasData = clientGroups.length > 0;

    return (
        <Grid>
            {/* ── Toolbar ── */}
            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                    <Tabs value={activeTab} onChange={handleChangeTab} sx={tabListStyle}>
                        <Tab disabled={showLoader} label="All Tickets" value="all-tickets" sx={tabStyle} />
                        <Tab disabled={showLoader} label="My Tickets" value="my-tickets" sx={tabStyle} />
                    </Tabs>
                </Grid>
                <Grid item xs={12} md={6} container
                    sx={{ justifyContent: { xs: 'flex-start', md: 'flex-end' }, alignItems: 'center' }}>
                    {isGreenUser && (
                        <IconButton onClick={handleZohoProductSync} style={{ marginRight: '10px', color: '#33a433' }} title="Sync Zoho Products Data">
                            <SyncIcon />
                        </IconButton>
                    )}
                    <Typography sx={{ pr: '5px', fontSize: '15px' }}>Search By:</Typography>
                    <SearchBox
                        value={queryParams.get('search') || ''}
                        placeholder="ID / Title / Desc / Client"
                        search={handleSearch}
                        isBrowserBackAction={isBackAction}
                    />
                    <IconButton onClick={() => setShowFilterDialog(true)} title="Apply Filter">
                        <FilterListIcon />
                    </IconButton>
                </Grid>
            </Grid>

            <Filter
                filterArea="Tickets"
                showFilterDialog={showFilterDialog}
                setShowFilterDialog={setShowFilterDialog}
                updateQueryParams={updateQueryParams}
                queryParams={queryParams}
                setIsSearchOrFilterApplied={setIsSearchOrFilterApplied}
                clearFilters={tabSwitch}
                handleTabSwitch={handleTabSwitch}
                checkForUrlFacility={false}
                checkForMyTickets={activeTab === 'my-tickets'}
                isBrowserBackAction={isBackAction}
            />

            {/* ── Table ── */}
            <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ ...HEAD_CELL, width: '11%' }}>Client Name</TableCell>
                            <TableCell sx={{ ...HEAD_CELL, width: '10%' }}>Facility</TableCell>
                            <TableCell sx={{ ...HEAD_CELL, width: '11%' }}>BD Owner</TableCell>
                            <TableCell sx={{ ...HEAD_CELL, width: '11%' }}>Project Owner</TableCell>
                            <TableCell sx={{ ...HEAD_CELL, width: '7%' }}>Ticket ID</TableCell>
                            <TableCell sx={{ ...HEAD_CELL, width: '16%' }}>Title</TableCell>
                            <TableCell sx={{ ...HEAD_CELL, width: '11%' }}>Assignee</TableCell>
                            <TableCell sx={{ ...HEAD_CELL, width: '10%' }}>Scheduled Date</TableCell>
                            <TableCell sx={{ ...HEAD_CELL, width: '9%' }}>Status</TableCell>
                            <TableCell sx={{ ...HEAD_CELL, width: '4%' }} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {showLoader ? (
                            <TableRow>
                                <TableCell colSpan={10} align="center" sx={{ py: 5 }}>
                                    <CircularProgress size={28} sx={{ color: '#008080' }} />
                                </TableCell>
                            </TableRow>
                        ) : !hasData ? (
                            <TableRow>
                                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                                    <Typography className="w3-text-grey">No Data Found</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            clientGroups.map(([clientName, tickets]) => (
                                <React.Fragment key={clientName}>
                                    {/* Group header row */}
                                    <TableRow sx={{ backgroundColor: '#eaf4f4' }}>
                                        <TableCell
                                            colSpan={10}
                                            sx={{ borderLeft: '4px solid #008080', py: 1, px: 2 }}
                                        >
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#1a1a1a' }}>
                                                    {clientName}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#666' }}>
                                                    {tickets.length} ticket{tickets.length !== 1 ? 's' : ''}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>

                                    {/* Ticket rows */}
                                    {tickets.map((ticket) => (
                                        <TableRow
                                            key={ticket.id}
                                            onClick={() => handleTicketClick(ticket.id)}
                                            sx={{
                                                cursor: 'pointer',
                                                verticalAlign: 'middle',
                                                '&:hover': { backgroundColor: '#f5fafa' },
                                            }}
                                        >
                                            <TableCell />
                                            <TableCell sx={{ fontSize: '13px' }}>
                                                {ticket.facility?.facility_name}
                                            </TableCell>
                                            <TableCell>
                                                <UserChip name={ticket.facility?.ex_data?.bd_owner?.name || null} />
                                            </TableCell>
                                            <TableCell>
                                                <UserChip name={ticket.facility?.ex_data?.project_owner?.name || null} />
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '13px', color: '#555' }}>
                                                {ticket.id}
                                            </TableCell>
                                            <TableCell sx={{ maxWidth: 220 }}>
                                                <Tooltip title={ticket.title || ''} placement="top">
                                                    <Typography noWrap sx={{ fontSize: '13px' }}>
                                                        {ticket.title}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <UserChip name={ticket.assignee} />
                                            </TableCell>
                                            <TableCell>
                                                <DateCell date={ticket.scheduled_date} />
                                            </TableCell>
                                            <TableCell>
                                                <StatusChip status={ticket.status} />
                                            </TableCell>
                                            <TableCell align="right">
                                                <ChevronRight sx={{ fontSize: 18, color: '#008080' }} />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </React.Fragment>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {hasData && !showLoader && (
                <Grid container sx={{ mt: 1.5, mb: 4 }}>
                    <Grid item xs={12} display="flex" justifyContent="flex-end" sx={{ mb: 1 }}>
                        <Typography sx={{ fontFamily: '"Helvetica Neue"', fontSize: '13px', color: '#3d3d3d' }}>
                            {Math.min((currentPage - 1) * ITEMS_PER_PAGE + 1, totalCount)}–
                            {Math.min(currentPage * ITEMS_PER_PAGE, totalCount)} of {totalCount}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} display="flex" justifyContent="center">
                        <Pagination
                            page={currentPage}
                            count={totalPages}
                            variant="outlined"
                            shape="rounded"
                            sx={paginationStyle}
                            onChange={handlePageChange}
                        />
                    </Grid>
                </Grid>
            )}
            {addingZohoPrdctLoader && <Loader open={addingZohoPrdctLoader} />}
        </Grid>
    );
}
